import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import "@fontsource/dm-sans/700.css";
import "@fontsource/jetbrains-mono/500.css";
import { Peer } from "./peer";
import {
  clearHistory,
  clearSession,
  loadHistory,
  loadSession,
  saveHistory,
  saveSession,
} from "./persistence";
import { SignalChannel } from "./signal";
import { TransferEngine } from "./transfer";
import { UI } from "./ui";
import type { ConnectionState, HistoryItem, Role } from "./types";

const SIGNAL_URL = __SIGNAL_URL__;

const root = document.getElementById("app");
if (!root) throw new Error("missing #app");

interface Session {
  role: Role;
  code: string;
  cid: string;
  signal: SignalChannel;
  peer: Peer | undefined;
  engine: TransferEngine | undefined;
  hasConnected: boolean;
  generation: number;
  lastRestartAt: number;
}

function newCid(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

const RESTART_THROTTLE_MS = 800;
const HISTORY_SAVE_MS = 250;
const MAX_CODE_REGEN = 5;

let session: Session | undefined;
let currentState: ConnectionState = "idle";
let historySaveTimer: number | undefined;
let codeRegenAttempts = 0;

const ui = new UI(root, {
  onCreate: () => startHost(),
  onJoin: (code) => startGuest(code),
  onCancel: () => endSession(),
  onSendText: (body) => session?.engine?.sendText(body),
  onSendFiles: (files) => {
    for (const f of files) session?.engine?.sendFile(f);
  },
});

function showNotice(title: string, body: string): void {
  // Tear down session state and bounce the user back to the landing screen
  // with a friendly notice card, instead of a hard error screen.
  cleanupSession();
  clearSession();
  clearHistory();
  ui.setHistory([]);
  currentState = "idle";
  ui.setState("idle");
  ui.showNotice({ title, body, cta: "Toss again" });
  // Auto-host a fresh code so the host card never visually contradicts the
  // notice (e.g. "code expired" while the card still shows the dead code).
  // The notice stays as the explanation; the user has a live code below it.
  startHost();
}

function setState(s: ConnectionState, err = ""): void {
  if (s !== "reconnecting") cancelReconnecting();
  currentState = s;
  ui.setState(s, err);
}

const RECONNECTING_DEBOUNCE_MS = 1500;
let reconnectingTimer: number | undefined;

function scheduleReconnecting(reason: string): void {
  // Only debounce the visible "reconnecting" transition from a healthy
  // state — gives transient drops (ICE rebind, brief WS hiccup, peer
  // refresh racing with their auto-reconnect) ~1.5s to recover quietly.
  if (currentState !== "connected" && currentState !== "connecting") return;
  if (reconnectingTimer !== undefined) return;
  reconnectingTimer = window.setTimeout(() => {
    reconnectingTimer = undefined;
    if (currentState === "connected" || currentState === "connecting") {
      currentState = "reconnecting";
      ui.setState("reconnecting", reason);
    }
  }, RECONNECTING_DEBOUNCE_MS);
}

function cancelReconnecting(): void {
  if (reconnectingTimer !== undefined) {
    clearTimeout(reconnectingTimer);
    reconnectingTimer = undefined;
  }
}

function generateCode(): string {
  // 9 digits: 1B space; collision still possible at scale, server detects.
  const buf = new Uint32Array(2);
  crypto.getRandomValues(buf);
  const n = (BigInt(buf[0]!) << 32n) | BigInt(buf[1]!);
  const mod = n % 1_000_000_000n;
  return mod.toString().padStart(9, "0");
}

function persistSession(): void {
  if (!session) return;
  saveSession({
    role: session.role,
    code: session.code,
    cid: session.cid,
    hasConnected: session.hasConnected,
  });
}

function scheduleHistorySave(): void {
  if (historySaveTimer !== undefined) return;
  historySaveTimer = window.setTimeout(() => {
    historySaveTimer = undefined;
    saveHistory(ui.getHistory());
  }, HISTORY_SAVE_MS);
}

function buildEngine(p: Peer): TransferEngine {
  return new TransferEngine({
    sendControl: (msg) => p.sendControl(JSON.stringify(msg)),
    sendBulk: (buf) => p.sendBulk(buf),
    waitForDrain: () => p.waitForDrain(),
    onItemAdded: (item) => {
      ui.appendItem(item);
      scheduleHistorySave();
      if (item.direction === "received") {
        ui.showToast(item.kind === "text" ? "caught" : `receiving ${item.name}`);
      }
    },
    onItemUpdate: (id, patch) => {
      ui.patchItem(id, patch as Partial<HistoryItem>);
      scheduleHistorySave();
    },
  });
}

function buildPeer(role: Role, gen: number): Peer {
  return new Peer(role, {
    onSdp: (sdp) => {
      if (session?.generation === gen) session.signal.send({ type: "sdp", sdp });
    },
    onIce: (candidate) => {
      if (session?.generation === gen) session.signal.send({ type: "ice", candidate });
    },
    onConnected: () => {
      if (session?.generation !== gen) return;
      const wasFirst = !session.hasConnected;
      session.hasConnected = true;
      if (wasFirst) persistSession();
      setState("connected");
      // Reflect the paired room's code in the URL on both sides — the host's
      // URL was empty until now, the catcher's was already `#<code>` from
      // joining; both converge to `#<partner-code>` once connected, so the
      // URL bar always identifies the active session.
      if (session.code) {
        history.replaceState(null, "", `#${session.code}`);
      }
    },
    onDisconnected: () => {
      if (!session || session.generation !== gen) return;
      if (currentState === "connected" || currentState === "connecting") {
        performRestart(true);
      }
    },
    onControl: (raw) => {
      if (session?.generation === gen) session.engine?.handleControl(raw);
    },
    onBulk: (buf) => {
      if (session?.generation === gen) session.engine?.handleBulk(buf);
    },
    onError: (err) => console.warn("peer error", err),
  });
}

function performRestart(initiate: boolean): void {
  if (!session) return;

  const now = Date.now();
  const inThrottleWindow = now - session.lastRestartAt < RESTART_THROTTLE_MS;
  const isRecovering = currentState === "reconnecting";
  if (inThrottleWindow && !isRecovering) return;
  session.lastRestartAt = now;

  session.generation += 1;
  const gen = session.generation;

  const old = session.peer;
  session.peer = undefined;
  session.engine = undefined;
  if (old) {
    try { old.close(); } catch {}
  }

  if (initiate) {
    try { session.signal.send({ type: "restart" }); } catch {}
  }

  const peer = buildPeer(session.role, gen);
  session.peer = peer;
  session.engine = buildEngine(peer);
  setState("connecting");

  if (session.role === "host") {
    peer.start().catch((err) => {
      console.error(err);
      showNotice("Connection hiccup.", "Try tossing again.");
    });
  }
}

function buildSignal(role: Role, code: string, cid: string): SignalChannel {
  return new SignalChannel(SIGNAL_URL, code, role, cid, {
    onReady: () => {
      if (!session) return;
      performRestart(false);
    },
    onRestart: () => {
      if (!session) return;
      performRestart(false);
    },
    onSdp: async (sdp) => {
      try {
        await session?.peer?.handleSdp(sdp);
      } catch (err) {
        console.error(err);
        showNotice("Connection hiccup.", "Try tossing again.");
      }
    },
    onIce: async (candidate) => {
      await session?.peer?.handleIce(candidate);
    },
    onPeerLeft: () => {
      teardownPeer();
      if (session?.hasConnected) {
        scheduleReconnecting("Peer left — waiting for them to come back");
      } else if (currentState === "hosting") {
        // host still waiting for first guest
      } else {
        setState("disconnected", "Peer left");
      }
    },
    onFull: () =>
      showNotice(
        "Couldn't find your friend.",
        "That code is already in use. Try a fresh one."
      ),
    onCodeTaken: () => {
      if (!session || session.role !== "host") {
        showNotice(
          "That code is already in use.",
          "Try catching with a different code."
        );
        return;
      }
      if (codeRegenAttempts >= MAX_CODE_REGEN) {
        showNotice(
          "Couldn't allocate a free code.",
          "Please try again."
        );
        return;
      }
      codeRegenAttempts += 1;
      beginHostSession();
    },
    onExpired: () =>
      showNotice(
        "Couldn't find your friend.",
        "That code expired or wasn't picked up."
      ),
    onClose: () => {
      scheduleReconnecting("Reconnecting…");
    },
    onError: () => {
      scheduleReconnecting("Reconnecting…");
    },
  });
}

function teardownPeer(): void {
  if (!session) return;
  session.generation += 1;
  try { session.peer?.close(); } catch {}
  session.peer = undefined;
  session.engine = undefined;
}

function beginHostSession(): void {
  cleanupSession();
  const code = generateCode();
  const cid = newCid();
  ui.setCode(code);
  setState("hosting");
  // The URL fragment is reserved for guest invites (`#<code>` means "join this code").
  // Don't leak the host's own code into the address bar, or reopening that URL in a
  // fresh tab/device with no sessionStorage would mis-route the host as a guest of
  // their own (now stale) code.
  history.replaceState(null, "", location.pathname);
  session = {
    role: "host",
    code,
    cid,
    signal: undefined as unknown as SignalChannel,
    peer: undefined,
    engine: undefined,
    hasConnected: false,
    generation: 0,
    lastRestartAt: 0,
  };
  session.signal = buildSignal("host", code, cid);
  persistSession();
}

function startHost(): void {
  clearHistory();
  ui.setHistory([]);
  codeRegenAttempts = 0;
  beginHostSession();
}

function startGuest(code: string): void {
  cleanupSession();
  clearHistory();
  ui.setHistory([]);
  const cid = newCid();
  ui.setCode(code);
  setState("connecting");
  session = {
    role: "guest",
    code,
    cid,
    signal: undefined as unknown as SignalChannel,
    peer: undefined,
    engine: undefined,
    hasConnected: false,
    generation: 0,
    lastRestartAt: 0,
  };
  session.signal = buildSignal("guest", code, cid);
  persistSession();
}

function resumeSession(role: Role, code: string, cid: string, hadConnected: boolean): void {
  cleanupSession();
  ui.setCode(code);
  if (role === "host") {
    history.replaceState(null, "", location.pathname);
    setState(
      hadConnected ? "reconnecting" : "hosting",
      hadConnected ? "Reconnecting…" : ""
    );
  } else {
    setState(
      hadConnected ? "reconnecting" : "connecting",
      hadConnected ? "Reconnecting…" : ""
    );
  }
  session = {
    role,
    code,
    cid,
    signal: undefined as unknown as SignalChannel,
    peer: undefined,
    engine: undefined,
    hasConnected: hadConnected,
    generation: 0,
    lastRestartAt: 0,
  };
  session.signal = buildSignal(role, code, cid);
}

function cleanupSession(): void {
  if (!session) return;
  try { session.signal.close(); } catch {}
  try { session.peer?.close(); } catch {}
  session = undefined;
}

function endSession(): void {
  cleanupSession();
  clearSession();
  clearHistory();
  if (historySaveTimer !== undefined) {
    clearTimeout(historySaveTimer);
    historySaveTimer = undefined;
  }
  currentState = "idle";
  history.replaceState(null, "", location.pathname);
  ui.reset();
  startHost();
}

window.addEventListener("paste", (e) => {
  if (!session?.engine) return;
  const text = e.clipboardData?.getData("text/plain");
  if (text && text.trim().length > 0) {
    const target = e.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === "TEXTAREA" || tag === "INPUT") return;
    e.preventDefault();
    session.engine.sendText(text);
    ui.showToast("Pasted text sent");
  }
});

window.addEventListener("beforeunload", () => {
  if (historySaveTimer !== undefined) {
    clearTimeout(historySaveTimer);
    historySaveTimer = undefined;
    saveHistory(ui.getHistory());
  }
  cleanupSession();
});

const persisted = loadSession();
if (persisted) {
  ui.setHistory(loadHistory());
  resumeSession(persisted.role, persisted.code, persisted.cid, persisted.hasConnected);
} else {
  const initialHash = location.hash.replace(/^#/, "").replace(/\D/g, "");
  if (/^[0-9]{9}$/.test(initialHash)) {
    startGuest(initialHash);
  } else {
    // Auto-host on first paint so the code + QR are immediately shareable.
    startHost();
  }
}
